import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import NextcloudFileBrowser from '../components/NextcloudFileBrowser'
import OneDriveFileBrowser from '../components/OneDriveFileBrowser'
import {
  Folder,
  FolderOpen,
  Share2,
  Users,
  Trash2,
  FileText,
  Plus,
  Cloud,
  Loader2,
  ChevronRight,
  Upload,
  MoreVertical,
  Pencil,
  Copy,
  RotateCcw,
  X,
  Download,
  Image as ImageIcon,
} from 'lucide-react'
import toast from 'react-hot-toast'

type TabId = 'meine' | 'geteilt' | 'gruppen' | 'papierkorb'

interface Overview {
  myFolders: number
  sharedWithMe: number
  groupFolders: number
  trash: number
}

interface FolderItem {
  id: number
  parent_id: number | null
  name: string
  owner_id: number
  created_at: string
  updated_at: string
}

export default function Dateien() {
  const [activeTab, setActiveTab] = useState<TabId>('meine')
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
  const [renameState, setRenameState] = useState<{ type: 'folder' | 'file'; id: number; name: string } | null>(null)
  const [shareState, setShareState] = useState<{ type: 'folder' | 'file'; id: number; name: string } | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [openBulkMenu, setOpenBulkMenu] = useState(false)
  const [previewFile, setPreviewFile] = useState<{ fileId: number; fileName: string; mimeType: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const selectKey = (key: string) => setSelectedKeys((prev) => new Set(prev).add(key))
  const deselectKey = (key: string) => setSelectedKeys((prev) => { const n = new Set(prev); n.delete(key); return n })
  const toggleSelect = (key: string) => setSelectedKeys((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n })
  const isSelected = (key: string) => selectedKeys.has(key)
  const clearSelection = () => setSelectedKeys(new Set())
  const selectAllKeys = (keys: string[]) => setSelectedKeys(new Set(keys))
  const selectedFolderIds = () => [...selectedKeys].filter((k) => k.startsWith('folder-')).map((k) => parseInt(k.replace('folder-', ''), 10))
  const selectedFileIds = () => [...selectedKeys].filter((k) => k.startsWith('file-')).map((k) => parseInt(k.replace('file-', ''), 10))

  const invalidateLists = () => {
    queryClient.invalidateQueries(['dateien-folders'])
    queryClient.invalidateQueries(['dateien-overview'])
    queryClient.invalidateQueries(['dateien-root'])
    queryClient.invalidateQueries(['dateien-trash'])
    if (selectedFolderId != null) queryClient.invalidateQueries(['dateien-folder', selectedFolderId])
  }

  useEffect(() => {
    if (!openMenuKey) return
    const close = (e: MouseEvent) => {
      if ((e.target as Element)?.closest?.('[data-dateien-menu]')) return
      setOpenMenuKey(null)
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [openMenuKey])

  useEffect(() => {
    if (!openBulkMenu) return
    const close = (e: MouseEvent) => {
      if ((e.target as Element)?.closest?.('[data-dateien-bulk-menu]')) return
      setOpenBulkMenu(false)
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [openBulkMenu])

  useEffect(() => {
    clearSelection()
  }, [activeTab, selectedFolderId])

  const { data: storageProviders } = useQuery(
    ['storage-providers'],
    async () => {
      const res = await api.get('/storage/providers')
      return res.data.data as {
        lernrakete: { enabled: boolean }
        nextcloud: { enabled: boolean; adminConfigured: boolean }
        onedrive: { enabled: boolean; adminConfigured: boolean }
      }
    },
    { retry: 1, refetchOnWindowFocus: false }
  )

  const { data: nextcloudStatus } = useQuery(
    ['nextcloud-status'],
    async () => {
      const res = await api.get('/nextcloud/status')
      return res.data.data as {
        enabled: boolean
        adminConfigured: boolean
        userConfigured: boolean
        username: string | null
        testStatus: string | null
      }
    },
    { enabled: storageProviders?.nextcloud?.enabled !== false, retry: 1, refetchOnWindowFocus: false }
  )

  const showNextcloudBrowser = !!storageProviders?.nextcloud?.enabled
    && !!nextcloudStatus?.adminConfigured
    && !!nextcloudStatus?.userConfigured

  const { data: onedriveStatus } = useQuery(
    ['onedrive-status'],
    async () => {
      const res = await api.get('/onedrive/status')
      return res.data.data as {
        enabled: boolean
        adminConfigured: boolean
        userConfigured: boolean
        accountEmail: string | null
        testStatus: string | null
      }
    },
    { enabled: storageProviders?.onedrive?.enabled !== false, retry: 1, refetchOnWindowFocus: false }
  )

  const showOnedriveBrowser = !!storageProviders?.onedrive?.enabled
    && !!onedriveStatus?.adminConfigured
    && !!onedriveStatus?.userConfigured
  const showLernraketeStorage = storageProviders?.lernrakete?.enabled !== false
  const showAnyCloudBrowser = showNextcloudBrowser || showOnedriveBrowser

  const { data: overview, isLoading: overviewLoading } = useQuery<Overview>(
    ['dateien-overview'],
    async () => {
      const res = await api.get('/dateien/overview')
      return res.data
    },
    { retry: 1, refetchOnWindowFocus: false }
  )

  const { data: rootFoldersRaw, isLoading: foldersLoading } = useQuery<FolderItem[]>(
    ['dateien-folders'],
    async () => {
      const res = await api.get('/dateien/folders')
      const data = res.data
      return Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : [])
    },
    { enabled: activeTab === 'meine' && showLernraketeStorage, retry: 1 }
  )
  const rootFolders = Array.isArray(rootFoldersRaw) ? rootFoldersRaw : []

  const { data: folderContents, isLoading: contentsLoading } = useQuery(
    ['dateien-folder', selectedFolderId],
    async () => {
      if (!selectedFolderId) return { subfolders: [], files: [] }
      const res = await api.get(`/dateien/folders/${selectedFolderId}`)
      return res.data
    },
    { enabled: activeTab === 'meine' && showLernraketeStorage && selectedFolderId != null, retry: 1 }
  )

  const { data: rootContents, isLoading: rootLoading } = useQuery(
    ['dateien-root'],
    async () => {
      const res = await api.get('/dateien/root')
      return res.data as { folders: FolderItem[]; files: { file_id: number; original_name: string }[] }
    },
    { enabled: activeTab === 'meine' && showLernraketeStorage && selectedFolderId == null, retry: 1 }
  )

  const { data: trashData, isLoading: trashLoading } = useQuery(
    ['dateien-trash'],
    async () => {
      const res = await api.get('/dateien/trash')
      return res.data as {
        folders: { id: number; name: string; deleted_at: string }[]
        files: { file_id: number; original_name: string; deleted_at: string }[]
      }
    },
    { enabled: activeTab === 'papierkorb', retry: 1 }
  )

  const createFolder = useMutation(
    async (payload: { name: string; parent_id?: number | null }) => {
      const res = await api.post('/dateien/folders', payload)
      return res.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['dateien-folders'])
        queryClient.invalidateQueries(['dateien-overview'])
        queryClient.invalidateQueries(['dateien-root'])
        setNewFolderName('')
        setShowNewFolder(false)
        toast.success('Ordner erstellt')
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error || 'Ordner konnte nicht erstellt werden')
      },
    }
  )

  const uploadFiles = useMutation(
    async (files: File[]) => {
      const form = new FormData()
      files.forEach((f) => form.append('files', f))
      if (selectedFolderId != null) form.append('folder_id', String(selectedFolderId))
      const res = await api.post('/dateien/upload', form)
      return res.data as { uploaded: { file_id: number; original_name: string }[] }
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['dateien-folders'])
        queryClient.invalidateQueries(['dateien-overview'])
        queryClient.invalidateQueries(['dateien-root'])
        if (selectedFolderId != null) queryClient.invalidateQueries(['dateien-folder', selectedFolderId])
        const n = data?.uploaded?.length ?? 0
        toast.success(n === 1 ? '1 Datei hochgeladen' : `${n} Dateien hochgeladen`)
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error || 'Upload fehlgeschlagen')
      },
    }
  )

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files
    if (!list?.length) return
    const files = Array.from(list)
    uploadFiles.mutate(files)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const list = e.dataTransfer.files
    if (!list?.length) return
    uploadFiles.mutate(Array.from(list))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const deleteFolder = useMutation(
    async (id: number) => {
      await api.delete(`/dateien/folders/${id}`)
    },
    { onSuccess: () => { setOpenMenuKey(null); invalidateLists(); toast.success('Ordner in den Papierkorb gelegt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Löschen fehlgeschlagen') } }
  )
  const deleteFile = useMutation(
    async (id: number) => {
      await api.delete(`/dateien/files/${id}`)
    },
    { onSuccess: () => { setOpenMenuKey(null); invalidateLists(); toast.success('Datei in den Papierkorb gelegt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Löschen fehlgeschlagen') } }
  )
  const renameFolder = useMutation(
    async ({ id, name }: { id: number; name: string }) => {
      await api.patch(`/dateien/folders/${id}`, { name })
    },
    { onSuccess: () => { setRenameState(null); setOpenMenuKey(null); invalidateLists(); toast.success('Ordner umbenannt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Umbenennen fehlgeschlagen') } }
  )
  const renameFile = useMutation(
    async ({ id, original_name }: { id: number; original_name: string }) => {
      await api.patch(`/dateien/files/${id}`, { original_name })
    },
    { onSuccess: () => { setRenameState(null); setOpenMenuKey(null); invalidateLists(); toast.success('Datei umbenannt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Umbenennen fehlgeschlagen') } }
  )
  const duplicateFolder = useMutation(
    async (id: number) => {
      await api.post(`/dateien/folders/${id}/duplicate`)
    },
    { onSuccess: () => { setOpenMenuKey(null); invalidateLists(); toast.success('Ordner dupliziert') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Duplizieren fehlgeschlagen') } }
  )
  const duplicateFile = useMutation(
    async (id: number) => {
      await api.post(`/dateien/files/${id}/duplicate`)
    },
    { onSuccess: () => { setOpenMenuKey(null); invalidateLists(); toast.success('Datei dupliziert') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Duplizieren fehlgeschlagen') } }
  )
  const createShare = useMutation(
    async (payload: { resource_type: 'folder' | 'file'; resource_id: number; shared_with_user_id: number; permission?: string; expires_at?: string }) => {
      await api.post('/dateien/share', payload)
    },
    { onSuccess: () => { setShareState(null); setOpenMenuKey(null); invalidateLists(); toast.success('Freigabe erstellt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Freigabe fehlgeschlagen') } }
  )

  const restoreFolder = useMutation(
    async (id: number) => { await api.post(`/dateien/folders/${id}/restore`) },
    { onSuccess: () => { invalidateLists(); toast.success('Ordner wiederhergestellt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Wiederherstellen fehlgeschlagen') } }
  )
  const restoreFile = useMutation(
    async (id: number) => { await api.post(`/dateien/files/${id}/restore`) },
    { onSuccess: () => { invalidateLists(); toast.success('Datei wiederhergestellt') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Wiederherstellen fehlgeschlagen') } }
  )
  const permanentDeleteFolder = useMutation<void, unknown, number>(
    async (id: number) => { await api.delete(`/dateien/trash/folders/${id}`) },
    { onSuccess: () => { invalidateLists(); toast.success('Ordner endgültig gelöscht') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Löschen fehlgeschlagen') } }
  )
  const permanentDeleteFile = useMutation<void, unknown, number>(
    async (id: number) => { await api.delete(`/dateien/trash/files/${id}`) },
    { onSuccess: () => { invalidateLists(); toast.success('Datei endgültig gelöscht') }, onError: (e: any) => { toast.error(e.response?.data?.error || 'Löschen fehlgeschlagen') } }
  )

  const runBulkDelete = async () => {
    const fIds = selectedFolderIds()
    const fileIds = selectedFileIds()
    try {
      await Promise.all([...fIds.map((id) => deleteFolder.mutateAsync(id)), ...fileIds.map((id) => deleteFile.mutateAsync(id))])
      clearSelection()
      setOpenBulkMenu(false)
      invalidateLists()
      toast.success(`${fIds.length + fileIds.length} Einträge in den Papierkorb gelegt`)
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Löschen fehlgeschlagen')
    }
  }
  const runBulkRestore = async () => {
    const fIds = selectedFolderIds()
    const fileIds = selectedFileIds()
    try {
      await Promise.all([...fIds.map((id) => restoreFolder.mutateAsync(id)), ...fileIds.map((id) => restoreFile.mutateAsync(id))])
      clearSelection()
      setOpenBulkMenu(false)
      invalidateLists()
      toast.success(`${fIds.length + fileIds.length} wiederhergestellt`)
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Wiederherstellen fehlgeschlagen')
    }
  }
  const runBulkPermanentDelete = async () => {
    if (!window.confirm('Ausgewählte Einträge endgültig löschen? Dies kann nicht rückgängig gemacht werden.')) return
    const fIds = selectedFolderIds()
    const fileIds = selectedFileIds()
    try {
      await Promise.all([...fIds.map((id) => permanentDeleteFolder.mutateAsync(id)), ...fileIds.map((id) => permanentDeleteFile.mutateAsync(id))])
      clearSelection()
      setOpenBulkMenu(false)
      invalidateLists()
      toast.success(`${fIds.length + fileIds.length} endgültig gelöscht`)
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Löschen fehlgeschlagen')
    }
  }
  const runBulkDuplicate = async () => {
    const fIds = selectedFolderIds()
    const fileIds = selectedFileIds()
    try {
      await Promise.all([...fIds.map((id) => duplicateFolder.mutateAsync(id)), ...fileIds.map((id) => duplicateFile.mutateAsync(id))])
      clearSelection()
      setOpenBulkMenu(false)
      invalidateLists()
      toast.success(`${fIds.length + fileIds.length} dupliziert`)
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Duplizieren fehlgeschlagen')
    }
  }

  const safeOverview = overview ?? {
    myFolders: 0,
    sharedWithMe: 0,
    groupFolders: 0,
    trash: 0,
  }

  const tabs = [
    { id: 'meine' as TabId, label: 'Meine Dateien', icon: FolderOpen, count: safeOverview.myFolders },
    { id: 'geteilt' as TabId, label: 'Mit mir geteilt', icon: Share2, count: safeOverview.sharedWithMe },
    { id: 'gruppen' as TabId, label: 'Gruppenordner', icon: Users, count: safeOverview.groupFolders },
    { id: 'papierkorb' as TabId, label: 'Papierkorb', icon: Trash2, count: safeOverview.trash },
  ]

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">Dateien</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Persönliche Dateien zur Unterrichtsvorbereitung – Ordner, Freigaben und Gruppenordner. Dateien werden mit kryptischen Dateinamen gespeichert.
        </p>
      </div>

      {((storageProviders?.nextcloud?.enabled && nextcloudStatus?.adminConfigured && !nextcloudStatus?.userConfigured) ||
        (storageProviders?.onedrive?.enabled && onedriveStatus?.adminConfigured && !onedriveStatus?.userConfigured)) && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/40">
          <Cloud className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Cloud-Speicher verbinden</p>
            <p className="mt-0.5 text-sm text-blue-800 dark:text-blue-300">
              In den{' '}
              <Link to="/settings?tab=personal" className="underline font-medium hover:text-blue-900 dark:hover:text-blue-100">
                persönlichen Einstellungen
              </Link>
              {nextcloudStatus?.adminConfigured && !nextcloudStatus?.userConfigured && ' Nextcloud (App-Passwort)'}
              {nextcloudStatus?.adminConfigured && !nextcloudStatus?.userConfigured && onedriveStatus?.adminConfigured && !onedriveStatus?.userConfigured && ' oder'}
              {onedriveStatus?.adminConfigured && !onedriveStatus?.userConfigured && ' OneDrive (Microsoft-Anmeldung)'}
              {' '}verbinden, um Dateien hier zu sehen.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Dateien-Bereiche">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id)
                  if (tab.id !== 'meine') setSelectedFolderId(null)
                }}
                className={`flex items-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      <div className="mt-6">
        {/* Meine Dateien */}
        {activeTab === 'meine' && (
          <div className="space-y-4">
            {showNextcloudBrowser && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Cloud className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  Meine Nextcloud-Dateien
                  {nextcloudStatus?.username && (
                    <span className="font-normal text-gray-500 dark:text-gray-400">({nextcloudStatus.username})</span>
                  )}
                </h2>
                <NextcloudFileBrowser />
              </section>
            )}

            {showOnedriveBrowser && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Cloud className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                  Meine OneDrive-Dateien
                  {onedriveStatus?.accountEmail && (
                    <span className="font-normal text-gray-500 dark:text-gray-400">({onedriveStatus.accountEmail})</span>
                  )}
                </h2>
                <OneDriveFileBrowser />
              </section>
            )}

            {showAnyCloudBrowser && showLernraketeStorage && (
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 pt-2 border-t border-gray-200 dark:border-gray-700">
                Lernrakete-Ordner
              </h2>
            )}

            {!showLernraketeStorage && !showAnyCloudBrowser && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                Kein Dateispeicher ist für Ihre Schule aktiviert. Bitte wenden Sie sich an die Administration.
              </p>
            )}

            {showLernraketeStorage && (
            <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {selectedFolderId != null && (
                  <>
                    <button
                      type="button"
                      onClick={() => setSelectedFolderId(null)}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      ← Wurzel
                    </button>
                    {folderContents?.folder?.parent_id != null && (
                      <button
                        type="button"
                        onClick={() => setSelectedFolderId(folderContents.folder.parent_id)}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        ← Übergeordneter Ordner
                      </button>
                    )}
                    {folderContents?.folder?.name && (
                      <span className="text-sm text-gray-700 dark:text-gray-300">/ {folderContents.folder.name}</span>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.mp3,.wav,.ogg,.mp4,.avi,.mov,.zip,.rar"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadFiles.isLoading}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  {uploadFiles.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Dateien hochladen
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewFolder(true)}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  <Plus className="h-4 w-4" />
                  Neuer Ordner
                </button>
              </div>
            </div>

            {showNewFolder && (
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/80">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Ordnername"
                  className="input input-sm flex-1 border-gray-300"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (newFolderName.trim()) createFolder.mutate({ name: newFolderName.trim(), parent_id: selectedFolderId })
                    }
                    if (e.key === 'Escape') setShowNewFolder(false)
                  }}
                />
                <button
                  type="button"
                  onClick={() => newFolderName.trim() && createFolder.mutate({ name: newFolderName.trim(), parent_id: selectedFolderId })}
                  disabled={!newFolderName.trim() || createFolder.isLoading}
                  className="btn btn-sm btn-primary"
                >
                  {createFolder.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Erstellen'}
                </button>
                <button type="button" onClick={() => setShowNewFolder(false)} className="btn btn-sm btn-ghost">
                  Abbrechen
                </button>
              </div>
            )}

            {overviewLoading || foldersLoading || (selectedFolderId == null && rootLoading) ? (
              <div className="flex items-center gap-2 py-8 text-gray-500 dark:text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Lade Ordner…</span>
              </div>
            ) : selectedFolderId == null ? (
              <div
                className={`relative rounded-lg border-2 border-dashed transition-colors ${isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30' : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/50'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {isDragging && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-primary-100/90 text-primary-700 dark:bg-primary-950/80 dark:text-primary-300">
                    <span className="text-sm font-medium">Dateien hier ablegen …</span>
                  </div>
                )}
                <ul className="divide-y divide-gray-200 rounded-lg border border-transparent bg-white dark:divide-gray-700 dark:bg-gray-900">
                {(() => {
                  const rootFolderList = Array.isArray(rootContents?.folders) ? rootContents.folders : rootFolders
                  const rootFileList = Array.isArray(rootContents?.files) ? rootContents.files : []
                  const allRootKeys = [...rootFolderList.map((x: { id: number }) => `folder-${x.id}`), ...rootFileList.map((x: { file_id: number }) => `file-${x.file_id}`)]
                  const allRootSelected = allRootKeys.length > 0 && allRootKeys.every((k) => selectedKeys.has(k))
                  return (
                    <>
                      {allRootKeys.length > 0 && (
                        <li className="flex items-center gap-2 px-2 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg dark:bg-gray-800/80 dark:border-gray-700">
                          <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={allRootSelected}
                              onChange={() => (allRootSelected ? clearSelection() : selectAllKeys(allRootKeys))}
                              className="rounded border-gray-300 dark:border-gray-600"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">Alle auswählen</span>
                          </label>
                          {selectedKeys.size > 0 && (
                            <div className="flex items-center gap-2 ml-4" data-dateien-bulk-menu>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{selectedKeys.size} ausgewählt</span>
                              <div className="relative">
                                <button type="button" onClick={() => setOpenBulkMenu(!openBulkMenu)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700" aria-label="Aktionen"><MoreVertical className="h-4 w-4" /></button>
                                {openBulkMenu && (
                                  <div className="absolute left-0 top-full z-20 mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-bulk-menu>
                                    <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={runBulkDelete}><Trash2 className="h-4 w-4" /> In Papierkorb legen</button>
                                    <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={runBulkDuplicate}><Copy className="h-4 w-4" /> Duplizieren</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </li>
                      )}
                    </>
                  )
                })()}
                {(rootContents?.folders?.length ?? rootFolders.length) === 0 && (rootContents?.files?.length ?? 0) === 0 && !showNewFolder && (
                  <li className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Noch keine Ordner oder Dateien. Erstellen Sie einen Ordner oder laden Sie Dateien hoch (Button oder Drag &amp; Drop).
                  </li>
                )}
                {(Array.isArray(rootContents?.folders) ? rootContents?.folders : rootFolders).map((f) => (
                  <li key={f.id} className="flex items-center gap-2 group">
                    <label className="flex-shrink-0 p-2 cursor-pointer" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected(`folder-${f.id}`)} onChange={() => toggleSelect(`folder-${f.id}`)} className="rounded border-gray-300 dark:border-gray-600" /></label>
                    <button
                      type="button"
                      onClick={() => setSelectedFolderId(f.id)}
                      className="flex flex-1 min-w-0 items-center gap-3 px-2 py-3 text-left hover:bg-gray-50 rounded-l-lg dark:hover:bg-gray-800/40"
                    >
                      <FolderOpen className="h-5 w-5 flex-shrink-0 text-amber-500" />
                      {renameState?.type === 'folder' && renameState?.id === f.id ? (
                        <input
                          className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          defaultValue={renameState.name}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameFolder.mutate({ id: f.id, name: (e.target as HTMLInputElement).value })
                            if (e.key === 'Escape') setRenameState(null)
                          }}
                          onBlur={(e) => { const v = (e.target as HTMLInputElement).value.trim(); if (v) renameFolder.mutate({ id: f.id, name: v }); setRenameState(null) }}
                        />
                      ) : (
                        <span className="font-medium text-gray-900 truncate dark:text-gray-100">{f.name}</span>
                      )}
                      <ChevronRight className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400" />
                    </button>
                    <div className="relative flex-shrink-0 pr-2" data-dateien-menu>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenMenuKey(openMenuKey === `folder-${f.id}` ? null : `folder-${f.id}`) }}
                        className="p-1.5 rounded hover:bg-gray-200 text-gray-500 dark:hover:bg-gray-700 dark:text-gray-400"
                        aria-label="Aktionen"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openMenuKey === `folder-${f.id}` && (
                        <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-menu>
                          <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setRenameState({ type: 'folder', id: f.id, name: f.name }); setOpenMenuKey(null) }}><Pencil className="h-4 w-4" /> Umbenennen</button>
                          <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setShareState({ type: 'folder', id: f.id, name: f.name }); setOpenMenuKey(null) }}><Share2 className="h-4 w-4" /> Teilen</button>
                          <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => duplicateFolder.mutate(f.id)}><Copy className="h-4 w-4" /> Duplizieren</button>
                          <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40" onClick={() => deleteFolder.mutate(f.id)}><Trash2 className="h-4 w-4" /> Löschen</button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                  {(Array.isArray(rootContents?.files) ? rootContents?.files : []).map((file) => (
                    <li key={file.file_id} className="flex items-center gap-2 group">
                      <label className="flex-shrink-0 p-2 cursor-pointer" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected(`file-${file.file_id}`)} onChange={() => toggleSelect(`file-${file.file_id}`)} className="rounded border-gray-300 dark:border-gray-600" /></label>
                      <button
                        type="button"
                        onClick={() => !renameState && setPreviewFile({ fileId: file.file_id, fileName: file.original_name, mimeType: (file as { mime_type?: string }).mime_type || 'application/octet-stream' })}
                        className="flex flex-1 min-w-0 items-center gap-3 px-2 py-3 text-left hover:bg-gray-50 rounded cursor-pointer dark:hover:bg-gray-800/40"
                      >
                        <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        {renameState?.type === 'file' && renameState?.id === file.file_id ? (
                          <input
                            className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            defaultValue={renameState.name}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameFile.mutate({ id: file.file_id, original_name: (e.target as HTMLInputElement).value })
                              if (e.key === 'Escape') setRenameState(null)
                            }}
                            onBlur={(e) => { const v = (e.target as HTMLInputElement).value.trim(); if (v) renameFile.mutate({ id: file.file_id, original_name: v }); setRenameState(null) }}
                          />
                        ) : (
                          <span className="text-gray-700 truncate dark:text-gray-300">{file.original_name}</span>
                        )}
                      </button>
                      <div className="relative flex-shrink-0 pr-2" data-dateien-menu>
                        <button
                          type="button"
                          onClick={() => setOpenMenuKey(openMenuKey === `file-${file.file_id}` ? null : `file-${file.file_id}`)}
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 dark:hover:bg-gray-700 dark:text-gray-400"
                          aria-label="Aktionen"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {openMenuKey === `file-${file.file_id}` && (
                          <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-menu>
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setRenameState({ type: 'file', id: file.file_id, name: file.original_name }); setOpenMenuKey(null) }}><Pencil className="h-4 w-4" /> Umbenennen</button>
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setShareState({ type: 'file', id: file.file_id, name: file.original_name }); setOpenMenuKey(null) }}><Share2 className="h-4 w-4" /> Teilen</button>
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => duplicateFile.mutate(file.file_id)}><Copy className="h-4 w-4" /> Duplizieren</button>
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40" onClick={() => deleteFile.mutate(file.file_id)}><Trash2 className="h-4 w-4" /> Löschen</button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
              </div>
            ) : (
              <div
                className={`relative rounded-lg border-2 border-dashed transition-colors ${isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30' : 'border-transparent'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {isDragging && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-primary-100/90 text-primary-700 dark:bg-primary-950/80 dark:text-primary-300">
                    <span className="text-sm font-medium">Dateien hier ablegen …</span>
                  </div>
                )}
              <div className="space-y-2">
                {contentsLoading ? (
                  <div className="flex items-center gap-2 py-4 text-gray-500 dark:text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Lade Inhalt…</span>
                  </div>
                ) : (
                  <>
                    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-900">
                      {(() => {
                        const subfolders = Array.isArray(folderContents?.subfolders) ? folderContents.subfolders : []
                        const subfiles = Array.isArray(folderContents?.files) ? folderContents.files : []
                        const allKeys = [...subfolders.map((x: FolderItem) => `folder-${x.id}`), ...subfiles.map((x: { file_id: number }) => `file-${x.file_id}`)]
                        const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k))
                        return allKeys.length > 0 ? (
                          <li className="flex items-center gap-2 px-2 py-2 bg-gray-50 border-b border-gray-200 dark:bg-gray-800/80 dark:border-gray-700">
                            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0"><input type="checkbox" checked={allSelected} onChange={() => (allSelected ? clearSelection() : selectAllKeys(allKeys))} className="rounded border-gray-300 dark:border-gray-600" /><span className="text-sm text-gray-600 dark:text-gray-400">Alle auswählen</span></label>
                            {selectedKeys.size > 0 && (
                              <div className="flex items-center gap-2 ml-4" data-dateien-bulk-menu>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{selectedKeys.size} ausgewählt</span>
                                <div className="relative">
                                  <button type="button" onClick={() => setOpenBulkMenu(!openBulkMenu)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700" aria-label="Aktionen"><MoreVertical className="h-4 w-4" /></button>
                                  {openBulkMenu && (
                                    <div className="absolute left-0 top-full z-20 mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-bulk-menu>
                                      <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={runBulkDelete}><Trash2 className="h-4 w-4" /> In Papierkorb legen</button>
                                      <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={runBulkDuplicate}><Copy className="h-4 w-4" /> Duplizieren</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </li>
                        ) : null
                      })()}
                      {(Array.isArray(folderContents?.subfolders) ? folderContents.subfolders : []).map((f: FolderItem) => (
                        <li key={f.id} className="flex items-center gap-2 group">
                          <label className="flex-shrink-0 p-2 cursor-pointer" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected(`folder-${f.id}`)} onChange={() => toggleSelect(`folder-${f.id}`)} className="rounded border-gray-300 dark:border-gray-600" /></label>
                          <button
                            type="button"
                            onClick={() => setSelectedFolderId(f.id)}
                            className="flex flex-1 min-w-0 items-center gap-3 px-2 py-3 text-left hover:bg-gray-50 rounded-l-lg dark:hover:bg-gray-800/40"
                          >
                            <Folder className="h-5 w-5 flex-shrink-0 text-amber-500" />
                            {renameState?.type === 'folder' && renameState?.id === f.id ? (
                              <input
                                className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                defaultValue={renameState.name}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') renameFolder.mutate({ id: f.id, name: (e.target as HTMLInputElement).value })
                                  if (e.key === 'Escape') setRenameState(null)
                                }}
                                onBlur={(e) => { const v = (e.target as HTMLInputElement).value.trim(); if (v) renameFolder.mutate({ id: f.id, name: v }); setRenameState(null) }}
                              />
                            ) : (
                              <span className="font-medium text-gray-900 truncate dark:text-gray-100">{f.name}</span>
                            )}
                            <ChevronRight className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400" />
                          </button>
                          <div className="relative flex-shrink-0 pr-2" data-dateien-menu>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setOpenMenuKey(openMenuKey === `folder-${f.id}` ? null : `folder-${f.id}`) }} className="p-1.5 rounded hover:bg-gray-200 text-gray-500 dark:hover:bg-gray-700 dark:text-gray-400" aria-label="Aktionen"><MoreVertical className="h-4 w-4" /></button>
                            {openMenuKey === `folder-${f.id}` && (
                              <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-menu>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setRenameState({ type: 'folder', id: f.id, name: f.name }); setOpenMenuKey(null) }}><Pencil className="h-4 w-4" /> Umbenennen</button>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setShareState({ type: 'folder', id: f.id, name: f.name }); setOpenMenuKey(null) }}><Share2 className="h-4 w-4" /> Teilen</button>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => duplicateFolder.mutate(f.id)}><Copy className="h-4 w-4" /> Duplizieren</button>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40" onClick={() => deleteFolder.mutate(f.id)}><Trash2 className="h-4 w-4" /> Löschen</button>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                      {(Array.isArray(folderContents?.files) ? folderContents.files : []).map((file: { file_id: number; original_name: string; mime_type?: string }) => (
                        <li key={file.file_id} className="flex items-center gap-2 group">
                          <label className="flex-shrink-0 p-2 cursor-pointer" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={isSelected(`file-${file.file_id}`)} onChange={() => toggleSelect(`file-${file.file_id}`)} className="rounded border-gray-300 dark:border-gray-600" /></label>
                          <button type="button" onClick={() => !renameState && setPreviewFile({ fileId: file.file_id, fileName: file.original_name, mimeType: file.mime_type || 'application/octet-stream' })} className="flex flex-1 min-w-0 items-center gap-3 px-2 py-3 text-left hover:bg-gray-50 rounded cursor-pointer dark:hover:bg-gray-800/40">
                            <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
                            {renameState?.type === 'file' && renameState?.id === file.file_id ? (
                              <input className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" defaultValue={renameState.name} autoFocus onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter') renameFile.mutate({ id: file.file_id, original_name: (e.target as HTMLInputElement).value }); if (e.key === 'Escape') setRenameState(null) }} onBlur={(e) => { const v = (e.target as HTMLInputElement).value.trim(); if (v) renameFile.mutate({ id: file.file_id, original_name: v }); setRenameState(null) }} />
                            ) : (
                              <span className="text-gray-700 truncate dark:text-gray-300">{file.original_name}</span>
                            )}
                          </button>
                          <div className="relative flex-shrink-0 pr-2" data-dateien-menu>
                            <button type="button" onClick={() => setOpenMenuKey(openMenuKey === `file-${file.file_id}` ? null : `file-${file.file_id}`)} className="p-1.5 rounded hover:bg-gray-200 text-gray-500 dark:hover:bg-gray-700 dark:text-gray-400" aria-label="Aktionen"><MoreVertical className="h-4 w-4" /></button>
                            {openMenuKey === `file-${file.file_id}` && (
                              <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-menu>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setRenameState({ type: 'file', id: file.file_id, name: file.original_name }); setOpenMenuKey(null) }}><Pencil className="h-4 w-4" /> Umbenennen</button>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => { setShareState({ type: 'file', id: file.file_id, name: file.original_name }); setOpenMenuKey(null) }}><Share2 className="h-4 w-4" /> Teilen</button>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => duplicateFile.mutate(file.file_id)}><Copy className="h-4 w-4" /> Duplizieren</button>
                                <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40" onClick={() => deleteFile.mutate(file.file_id)}><Trash2 className="h-4 w-4" /> Löschen</button>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {(Array.isArray(folderContents?.subfolders) ? folderContents.subfolders : []).length === 0 && (Array.isArray(folderContents?.files) ? folderContents.files : []).length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">Dieser Ordner ist leer.</p>
                    )}
                  </>
                )}
              </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Mit mir geteilt */}
        {activeTab === 'geteilt' && (
          <div className="card p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <Share2 className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="mt-2">Mit Ihnen geteilte Ordner und Dateien erscheinen hier.</p>
            <p className="mt-1">Freigaben können mit Ablaufdatum eingerichtet werden.</p>
          </div>
        )}

        {/* Gruppenordner */}
        {activeTab === 'gruppen' && (
          <div className="card p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <Users className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="mt-2">Gruppenordner mit Rechtemanagement erscheinen hier.</p>
          </div>
        )}

        {/* Papierkorb */}
        {activeTab === 'papierkorb' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Gelöschte Ordner und Dateien. Wiederherstellen oder endgültig entfernen.
            </p>
            {trashLoading ? (
              <div className="flex items-center gap-2 py-8 text-gray-500 dark:text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Lade Papierkorb…</span>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(() => {
                    const trashFolders = Array.isArray(trashData?.folders) ? trashData.folders : []
                    const trashFiles = Array.isArray(trashData?.files) ? trashData.files : []
                    const allTrashKeys = [...trashFolders.map((x: { id: number }) => `folder-${x.id}`), ...trashFiles.map((x: { file_id: number }) => `file-${x.file_id}`)]
                    const allTrashSelected = allTrashKeys.length > 0 && allTrashKeys.every((k) => selectedKeys.has(k))
                    return allTrashKeys.length > 0 ? (
                      <li className="flex items-center gap-2 px-2 py-2 bg-gray-50 border-b border-gray-200 dark:bg-gray-800/80 dark:border-gray-700">
                        <label className="flex items-center gap-2 cursor-pointer flex-shrink-0"><input type="checkbox" checked={allTrashSelected} onChange={() => (allTrashSelected ? clearSelection() : selectAllKeys(allTrashKeys))} className="rounded border-gray-300 dark:border-gray-600" /><span className="text-sm text-gray-600 dark:text-gray-400">Alle auswählen</span></label>
                        {selectedKeys.size > 0 && (
                          <div className="flex items-center gap-2 ml-4" data-dateien-bulk-menu>
                            <span className="text-sm text-gray-600 dark:text-gray-400">{selectedKeys.size} ausgewählt</span>
                            <div className="relative">
                              <button type="button" onClick={() => setOpenBulkMenu(!openBulkMenu)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700" aria-label="Aktionen"><MoreVertical className="h-4 w-4" /></button>
                              {openBulkMenu && (
                                <div className="absolute left-0 top-full z-20 mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" data-dateien-bulk-menu>
                                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800" onClick={runBulkRestore}><RotateCcw className="h-4 w-4" /> Wiederherstellen</button>
                                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40" onClick={runBulkPermanentDelete}><Trash2 className="h-4 w-4" /> Endgültig löschen</button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </li>
                    ) : null
                  })()}
                  {(Array.isArray(trashData?.folders) ? trashData.folders : []).map((f) => (
                    <li key={`folder-${f.id}`} className="flex items-center gap-3 px-4 py-3">
                      <label className="flex-shrink-0 cursor-pointer"><input type="checkbox" checked={isSelected(`folder-${f.id}`)} onChange={() => toggleSelect(`folder-${f.id}`)} className="rounded border-gray-300 dark:border-gray-600" /></label>
                      <Folder className="h-5 w-5 flex-shrink-0 text-amber-500" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{f.name}</span>
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                          {f.deleted_at ? new Date(f.deleted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => restoreFolder.mutate(f.id)}
                          disabled={restoreFolder.isLoading}
                          className="inline-flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                          title="Wiederherstellen"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Wiederherstellen
                        </button>
                        <button
                          type="button"
                          onClick={() => window.confirm('Ordner endgültig löschen?') && permanentDeleteFolder.mutate(f.id)}
                          disabled={permanentDeleteFolder.isLoading}
                          className="inline-flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                          title="Endgültig löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                          Endgültig löschen
                        </button>
                      </div>
                    </li>
                  ))}
                  {(Array.isArray(trashData?.files) ? trashData.files : []).map((file) => (
                    <li key={`file-${file.file_id}`} className="flex items-center gap-3 px-4 py-3">
                      <label className="flex-shrink-0 cursor-pointer"><input type="checkbox" checked={isSelected(`file-${file.file_id}`)} onChange={() => toggleSelect(`file-${file.file_id}`)} className="rounded border-gray-300 dark:border-gray-600" /></label>
                      <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-700 truncate block dark:text-gray-300">{file.original_name}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {file.deleted_at ? new Date(file.deleted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => restoreFile.mutate(file.file_id)}
                          disabled={restoreFile.isLoading}
                          className="inline-flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                          title="Wiederherstellen"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Wiederherstellen
                        </button>
                        <button
                          type="button"
                          onClick={() => window.confirm('Datei endgültig löschen?') && permanentDeleteFile.mutate(file.file_id)}
                          disabled={permanentDeleteFile.isLoading}
                          className="inline-flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                          title="Endgültig löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                          Endgültig löschen
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                {(Array.isArray(trashData?.folders) ? trashData.folders : []).length === 0 && (Array.isArray(trashData?.files) ? trashData.files : []).length === 0 && (
                  <div className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    <Trash2 className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
                    <p className="mt-2">Papierkorb ist leer.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Teilen-Modal */}
      {shareState && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 dark:bg-black/70" onClick={() => setShareState(null)}>
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-xl max-w-sm w-full mx-4 dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Teilen: {shareState.name}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Benutzer-ID des Nutzers eingeben, mit dem geteilt werden soll.</p>
            <ShareForm
              resourceType={shareState.type}
              resourceId={shareState.id}
              onSubmit={(userId) => createShare.mutate({ resource_type: shareState.type, resource_id: shareState.id, shared_with_user_id: userId })}
              onCancel={() => setShareState(null)}
              isLoading={createShare.isLoading}
            />
          </div>
        </div>
      )}

      {/* Datei-Vorschau (Bild, PDF) – wie in Akte/DocumentViewer */}
      {previewFile && (
        <DateienFilePreview
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          fileId={previewFile.fileId}
          fileName={previewFile.fileName}
          mimeType={previewFile.mimeType}
        />
      )}
    </div>
  )
}

function ShareForm({
  resourceType,
  resourceId,
  onSubmit,
  onCancel,
  isLoading,
}: {
  resourceType: 'folder' | 'file'
  resourceId: number
  onSubmit: (sharedWithUserId: number) => void
  onCancel: () => void
  isLoading: boolean
}) {
  const [userId, setUserId] = useState('')
  return (
    <form className="mt-4 flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); const n = parseInt(userId, 10); if (!isNaN(n)) onSubmit(n) }}>
      <input type="number" min={1} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Benutzer-ID" className="input border border-gray-300 rounded-md px-3 py-2" />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn btn-ghost">Abbrechen</button>
        <button type="submit" disabled={!userId.trim() || isLoading} className="btn btn-primary">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null} Freigabe erstellen
        </button>
      </div>
    </form>
  )
}

const OFFICE_MIMES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-word',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
]
const OFFICE_EXT = ['.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt']
function isOfficeFile(mimeType: string, fileName: string): boolean {
  const m = (mimeType || '').toLowerCase()
  if (OFFICE_MIMES.some((x) => m === x.toLowerCase() || m.includes('spreadsheet') || m.includes('wordprocessing') || m.includes('presentation'))) return true
  const ext = (fileName || '').toLowerCase()
  return OFFICE_EXT.some((e) => ext.endsWith(e))
}

/** Vorschau für Dateien (Bild, PDF, Office via OnlyOffice) – Anlehnung an DocumentViewer in Akte */
function DateienFilePreview({
  isOpen,
  onClose,
  fileId,
  fileName,
  mimeType,
}: {
  isOpen: boolean
  onClose: () => void
  fileId: number
  fileName: string
  mimeType: string
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [onlyofficeConfig, setOnlyofficeConfig] = useState<{
    docServerUrl: string
    documentType: string
    document: { url: string; key: string; fileType: string; title: string }
    editorConfig: { callbackUrl: string }
    token?: string | null
  } | null>(null)
  const [onlyofficeReady, setOnlyofficeReady] = useState(false)
  const editorDivId = 'onlyoffice-editor-' + fileId
  const base = api.defaults.baseURL || '/api'
  const viewUrl = `${base}/dateien/files/${fileId}/view`
  const downloadUrl = `${base}/dateien/files/${fileId}/download`
  const isPDF = mimeType === 'application/pdf'
  const isImage = mimeType?.startsWith('image/') ?? false
  const isOffice = isOfficeFile(mimeType, fileName)

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      setError(false)
      setOnlyofficeConfig(null)
      setOnlyofficeReady(false)
    }
  }, [isOpen, fileId])

  useEffect(() => {
    if (!isOpen || !isOffice || !fileId) return
    let cancelled = false
    api
      .get(`/dateien/files/${fileId}/onlyoffice-config`, { params: { debug: 1 } })
      .then((res) => {
        if (cancelled) return
        const data = res.data as {
          docServerUrl?: string
          documentType?: string
          document?: { url: string; key: string; fileType: string; title: string }
          editorConfig?: { callbackUrl: string }
          token?: string | null
          debug?: Record<string, unknown>
          enabled?: boolean
        }
        if (data?.debug) {
          // Hilft bei Produktion ohne direkten Backend-Logzugriff.
          console.info('[OnlyOffice debug]', data.debug)
        }
        if (data?.docServerUrl && data?.document && data?.editorConfig) {
          setOnlyofficeConfig({
            docServerUrl: data.docServerUrl,
            documentType: data.documentType || 'word',
            document: data.document,
            editorConfig: data.editorConfig,
            token: data.token ?? null,
          })
        } else {
          setOnlyofficeConfig(null)
          setLoading(false)
        }
      })
      .catch((e) => {
        console.error('[OnlyOffice config error]', e?.response?.data || e?.message || e)
        if (!cancelled) {
          setOnlyofficeConfig(null)
          setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [isOpen, isOffice, fileId])

  useEffect(() => {
    if (!onlyofficeConfig || !isOpen) return
    const config = onlyofficeConfig
    const docServerUrl = config.docServerUrl.replace(/\/$/, '')
    const scriptUrl = `${docServerUrl}/web-apps/apps/api/documents/api.js`
    const existing = document.querySelector(`script[src="${scriptUrl}"]`)
    if (existing) {
      initOnlyOfficeEditor()
      return
    }
    const script = document.createElement('script')
    script.src = scriptUrl
    script.type = 'text/javascript'
    script.onload = () => initOnlyOfficeEditor()
    script.onerror = () => {
      setOnlyofficeReady(false)
      setLoading(false)
    }
    document.head.appendChild(script)
    return () => {
      const docApi = (window as unknown as { DocsAPI?: { DocEditor?: unknown } }).DocsAPI
      if (docApi?.DocEditor && typeof (docApi as { DocEditor: (id: string) => { destroyEditor: () => void } }).DocEditor === 'function') {
        try {
          const ed = (docApi as { DocEditor: (id: string) => { destroyEditor: () => void } }).DocEditor(editorDivId)
          if (ed?.destroyEditor) ed.destroyEditor()
        } catch (_) {}
      }
    }
    function initOnlyOfficeEditor() {
      const DocsAPI = (window as unknown as { DocsAPI?: { DocEditor: new (id: string, config: object) => void } }).DocsAPI
      if (!DocsAPI?.DocEditor) {
        setLoading(false)
        return
      }
      try {
        const editorConfig: Record<string, unknown> = {
          documentType: config.documentType,
          document: config.document,
          editorConfig: config.editorConfig,
        }
        if (config.token) editorConfig.token = config.token
        new DocsAPI.DocEditor(editorDivId, editorConfig)
        setOnlyofficeReady(true)
        setLoading(false)
      } catch (e) {
        setLoading(false)
      }
    }
  }, [onlyofficeConfig, isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleDownload = () => {
    window.open(downloadUrl, '_blank')
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 dark:bg-black/70 p-2" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[96vw] max-w-[1800px] h-[96vh] max-h-[96vh] flex flex-col dark:bg-gray-900 dark:shadow-black/40" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0 dark:border-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            {isPDF && <FileText className="h-5 w-5 text-red-600 flex-shrink-0 dark:text-red-400" />}
            {isImage && <ImageIcon className="h-5 w-5 text-blue-600 flex-shrink-0 dark:text-blue-400" />}
            <span className="text-lg font-medium text-gray-900 truncate dark:text-gray-100" title={fileName}>{fileName}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
              <Download className="h-4 w-4" />
              Herunterladen
            </a>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 dark:hover:bg-gray-800 dark:text-gray-400" title="Schließen (ESC)">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden relative flex items-center justify-center bg-gray-100 min-h-0 dark:bg-gray-950">
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-white dark:bg-gray-900">
              <FileText className="h-16 w-16 text-gray-300 mb-4 dark:text-gray-600" />
              <p className="text-gray-600 mb-2 dark:text-gray-400">Vorschau konnte nicht geladen werden</p>
              <button type="button" onClick={handleDownload} className="btn btn-primary inline-flex items-center gap-2">
                <Download className="h-4 w-4" /> Herunterladen
              </button>
            </div>
          )}
          {!error && isPDF && (
            <>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
                </div>
              )}
              <iframe
                key={`pdf-${fileId}`}
                src={viewUrl}
                className="w-full h-full border-0 rounded-b-xl"
                title={fileName}
                onLoad={() => setLoading(false)}
                onError={() => { setLoading(false); setError(true) }}
              />
            </>
          )}
          {!error && isImage && (
            <>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
                </div>
              )}
              <img
                key={`img-${fileId}`}
                src={viewUrl}
                alt={fileName}
                className="max-w-full max-h-full object-contain"
                onLoad={() => setLoading(false)}
                onError={() => { setLoading(false); setError(true) }}
              />
            </>
          )}
          {!error && isOffice && loading && !onlyofficeConfig && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
            </div>
          )}
          {!error && isOffice && onlyofficeConfig && (
            <>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
                </div>
              )}
              <div
                id={editorDivId}
                className="w-full h-full min-h-[60vh]"
                style={{ display: loading ? 'none' : 'block' }}
              />
            </>
          )}
          {!error && !isPDF && !isImage && (!isOffice || (isOffice && !onlyofficeConfig && !loading)) && (
            <div className="flex flex-col items-center justify-center p-8">
              <FileText className="h-16 w-16 text-gray-400 mb-4 dark:text-gray-500" />
              <p className="text-gray-600 mb-2 dark:text-gray-400">
                {isOffice ? 'OnlyOffice nicht konfiguriert oder nicht erreichbar.' : 'Vorschau für diesen Dateityp nicht verfügbar'}
              </p>
              <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{mimeType}</p>
              <button type="button" onClick={handleDownload} className="btn btn-primary inline-flex items-center gap-2">
                <Download className="h-4 w-4" /> Herunterladen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
